import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { CollectionScoringStatusView } from '../../generated';
import { getBenchmarkSelfScoringStatusV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdScoringsScoringIdGet } from '../../generated';
import type { BenchmarkScoringStatus } from '../list-benchmark-scorings';

/*
 * GET /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}/scorings/{scoring_id}: the status of one scoring,
 * polled while it is pending. It answers the generated `CollectionScoringStatusView`. Tests answer this path with
 * `tests/mocks/benchmark-handlers.ts`.
 */

/** Where a scoring stands, mapped for the UI. */
export interface BenchmarkScoringProgress {
  id: string;
  productCategoryId: string;
  /** Pending for 30 minutes turns failed; a late result can turn it ready again. */
  status: BenchmarkScoringStatus;
  requestedAt: string;
  readyAt: string | null;
  failureReason: string | null;
  scoredImages: number;
  totalImages: number;
}

export function toBenchmarkScoringProgress(response: CollectionScoringStatusView): BenchmarkScoringProgress {
  return {
    id: response.id,
    productCategoryId: response.product_category_id,
    status: response.status,
    requestedAt: response.requested_at,
    readyAt: response.ready_at,
    failureReason: response.failure_reason,
    scoredImages: response.scored_images,
    totalImages: response.total_images,
  };
}

export interface GetBenchmarkScoringQueryVariables {
  workspace_id: string;
  benchmark_id: string;
  scoring_id: string;
}

export type GetBenchmarkScoringQuery = BenchmarkScoringProgress;

async function getBenchmarkScoring({ workspace_id, benchmark_id, scoring_id }: GetBenchmarkScoringQueryVariables) {
  const { data } = await getBenchmarkSelfScoringStatusV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdScoringsScoringIdGet({
    path: { workspace_id, benchmark_id, scoring_id },
    throwOnError: true,
  });
  return toBenchmarkScoringProgress(data);
}

export const useGetBenchmarkScoringQuery = <TData = GetBenchmarkScoringQuery, TError = unknown>(
  variables: GetBenchmarkScoringQueryVariables,
  options?: Omit<UseQueryOptions<GetBenchmarkScoringQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetBenchmarkScoringQuery, TError, TData>({
    queryKey: useGetBenchmarkScoringQuery.getKey(variables),
    queryFn: () => getBenchmarkScoring(variables),
    ...options,
  });
};

useGetBenchmarkScoringQuery.getKey = ({
  workspace_id,
  benchmark_id,
  scoring_id,
}: GetBenchmarkScoringQueryVariables) => ['GetBenchmarkScoring', { workspace_id, benchmark_id, scoring_id }];

useGetBenchmarkScoringQuery.fetcher = (variables: GetBenchmarkScoringQueryVariables) => () =>
  getBenchmarkScoring(variables);
